import {
  type BudgetStatus,
  type Goal,
  isNegativeMoney,
  type MemberLimit,
  negateMoney,
  subtractMoney,
  toBaseAmount,
} from '@familywallet/shared';
import { ChevronRight, PiggyBank, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCan } from '../lib/auth';
import { useBudgets, useGoals, useLimits } from '../lib/ledger';
import { formatAmount } from '../lib/money';
import { cn } from '../lib/utils';
import { Card, ListGroup, SectionLabel } from './ui/card';

const FILL: Record<BudgetStatus | 'goal', string> = {
  normal: 'bg-accent',
  warning: 'bg-expense',
  over: 'bg-danger',
  goal: 'bg-accent',
};

/**
 * An 8px progress bar (track `--line`): accent while normal, `--expense` from 80% (warning),
 * `--danger` at 100% and beyond (over budget). The state is also said in words next to it.
 */
export function StatusBar({
  percent,
  status,
  label,
}: {
  percent: string | null;
  status: BudgetStatus | 'goal';
  label: string;
}) {
  const shown = percent ?? '0';
  return (
    <div
      className="h-2 overflow-hidden rounded-full bg-line"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Number.parseInt(shown, 10)}
    >
      <div className={cn('h-full rounded-full', FILL[status])} style={{ width: `${shown}%` }} />
    </div>
  );
}

/** "$5.00 left" or "$3.00 over budget" (over in `--danger`, the only red here). */
export function RemainingText({
  remaining,
  currency,
  overKey,
}: {
  remaining: string;
  currency: string;
  overKey: string;
}) {
  const { t, i18n } = useTranslation();
  const over = isNegativeMoney(remaining);
  const amount = formatAmount(over ? negateMoney(remaining) : remaining, currency, i18n.language);
  return (
    <span dir="auto" className={cn('text-[13px]', over ? 'text-danger' : 'text-muted')}>
      {over ? t(overKey, { amount }) : t('budgets.left', { amount })}
    </span>
  );
}

/** "Over limit" / "Over budget": a `--surface-2` pill with `--danger` text. */
export function OverBadge({ label }: { label: string }) {
  return (
    <span className="shrink-0 rounded-full bg-surface-2 px-2.5 py-1 text-xs font-semibold text-danger">
      {label}
    </span>
  );
}

/** "Budget this month" on Home, for whoever may see budgets; hidden until a budget exists. */
export function BudgetHomeCard() {
  const { t, i18n } = useTranslation();
  const canView = useCan('budgets.view');
  const budgets = useBudgets(undefined, canView);
  const data = budgets.data;
  if (!canView || !data) return null;
  const overall = data.overall;
  const withBudget = data.categories.filter((c) => c.amount !== null);
  if (overall.amount === null && withBudget.length === 0) return null;
  const over = withBudget.filter((c) => c.status === 'over').length;
  return (
    <Card className="flex flex-col gap-3">
      <Link
        to="/budgets"
        className="-m-1 flex min-h-11 items-center gap-2 rounded-xl p-1 focus-visible:outline-2 focus-visible:outline-accent"
      >
        <h2 className="flex-1 text-[15px] font-semibold">{t('budgets.homeTitle')}</h2>
        {overall.status === 'over' && <OverBadge label={t('budgets.overBadge')} />}
        <ChevronRight aria-hidden="true" className="size-5 text-muted rtl:rotate-180" />
      </Link>
      {overall.amount !== null && overall.remaining !== null ? (
        <>
          <div className="flex items-baseline justify-between gap-3">
            <span dir="ltr" className="text-lg font-semibold tabular-nums">
              {t('budgets.spentOf', {
                spent: formatAmount(overall.spent, data.currency, i18n.language),
                amount: formatAmount(overall.amount, data.currency, i18n.language),
              })}
            </span>
            <RemainingText
              remaining={overall.remaining}
              currency={data.currency}
              overKey="budgets.over"
            />
          </div>
          <StatusBar
            percent={overall.percent}
            status={overall.status ?? 'normal'}
            label={t('budgets.overall')}
          />
        </>
      ) : (
        <span className="text-[13px] text-muted">
          {t('budgets.spentOnly', {
            spent: formatAmount(overall.spent, data.currency, i18n.language),
          })}
        </span>
      )}
      {over > 0 && (
        <span className="text-[13px] text-danger">
          {t('budgets.categoriesOver', { count: over })}
        </span>
      )}
    </Card>
  );
}

/** A child's own "Left this month" on Home; hidden without a limit. */
export function MyLimitCard({ memberId }: { memberId: string }) {
  const { t, i18n } = useTranslation();
  const limits = useLimits();
  const mine = limits.data?.items.find((l) => l.memberId === memberId);
  if (!limits.data || !mine || mine.amount === null || mine.left === null) return null;
  const currency = limits.data.currency;
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-[15px] font-semibold">{t('limits.homeTitle')}</h2>
        {mine.over && <OverBadge label={t('limits.over')} />}
      </div>
      <div className="flex flex-col">
        <span
          dir="ltr"
          className={cn(
            'text-2xl font-semibold tabular-nums',
            mine.over ? 'text-danger' : 'text-fg',
          )}
        >
          {mine.over
            ? t('limits.overBy', {
                amount: formatAmount(negateMoney(mine.left), currency, i18n.language),
              })
            : formatAmount(mine.left, currency, i18n.language)}
        </span>
        <span className="text-[13px] text-muted">
          {t('limits.of', { amount: formatAmount(mine.amount, currency, i18n.language) })}
        </span>
      </div>
      <StatusBar
        percent={mine.percent}
        status={mine.status ?? 'normal'}
        label={t('limits.title')}
      />
    </Card>
  );
}

/** One child's limit as a list row (Home for parents, the Members screen). */
function ChildLimitRow({ item, currency }: { item: MemberLimit; currency: string }) {
  const { t, i18n } = useTranslation();
  return (
    <li>
      <Link
        to={`/members/${item.memberId}`}
        className="flex min-h-16 flex-col gap-2 px-4 py-3 hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
      >
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[15px] font-medium">
            {item.displayName}
          </span>
          {item.over && <OverBadge label={t('limits.over')} />}
          <span dir="ltr" className="shrink-0 text-[13px] text-muted tabular-nums">
            {t('budgets.spentOf', {
              spent: formatAmount(item.spent, currency, i18n.language),
              amount: formatAmount(item.amount!, currency, i18n.language),
            })}
          </span>
        </span>
        <StatusBar
          percent={item.percent}
          status={item.status ?? 'normal'}
          label={t('limits.title')}
        />
      </Link>
    </li>
  );
}

/** Parents on Home: each child with a limit, "Over limit" when they went over. */
export function ChildrenLimitsCard() {
  const { t } = useTranslation();
  const canManage = useCan('limits.manage');
  const limits = useLimits();
  if (!canManage || !limits.data) return null;
  const items = limits.data.items.filter((l) => l.amount !== null);
  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>{t('limits.childrenTitle')}</SectionLabel>
      <ListGroup>
        {items.map((item) => (
          <ChildLimitRow key={item.memberId} item={item} currency={limits.data.currency} />
        ))}
      </ListGroup>
    </section>
  );
}

/** A goal's progress: saved of target, the bar, and what is left or reached. */
export function GoalProgress({ goal }: { goal: Goal }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <span dir="ltr" className="text-[13px] text-muted tabular-nums">
        {t('goals.saved', {
          saved: formatAmount(goal.saved, goal.currency, i18n.language),
          target: formatAmount(goal.targetAmount, goal.currency, i18n.language),
        })}
      </span>
      <StatusBar percent={goal.percent} status="goal" label={t('goals.progress')} />
    </div>
  );
}

/** The goals strip on Home: a card per goal, scrolling sideways; hidden without goals. */
export function GoalsStrip() {
  const { t } = useTranslation();
  const goals = useGoals();
  const items = goals.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <SectionLabel>{t('goals.homeTitle')}</SectionLabel>
        <Link
          to="/goals"
          className="inline-flex min-h-11 items-center rounded-xl px-1 text-[13px] font-semibold text-accent focus-visible:outline-2 focus-visible:outline-accent"
        >
          {t('home.seeAll')}
        </Link>
      </div>
      <ul className="flex snap-x gap-2 overflow-x-auto pb-1">
        {items.map((goal) => (
          <li key={goal.id} className="w-[200px] shrink-0 snap-start">
            <Link
              to={`/goals/${goal.id}`}
              className="flex h-full flex-col gap-2 rounded-[20px] border border-line bg-surface p-4 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
            >
              <span className="flex items-center gap-2">
                <PiggyBank aria-hidden="true" className="size-5 text-accent" strokeWidth={2} />
                <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{goal.name}</span>
              </span>
              <GoalProgress goal={goal} />
              <span className="text-xs text-muted">
                {goal.reached ? t('goals.reached') : `${goal.percent}%`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Before saving an expense: a clear warning when it takes the person over their own monthly
 * limit (children). Saving still works; parents see "Over limit". Other currencies are
 * estimated at today's family rate, as the amount preview does.
 */
export function LimitWarning({
  memberId,
  amount,
  currency,
  baseCurrency,
  rate,
}: {
  memberId: string;
  amount: string | null;
  currency: string;
  baseCurrency: string;
  rate: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const limits = useLimits();
  const mine = limits.data?.items.find((l) => l.memberId === memberId);
  if (!amount || !mine || mine.amount === null || mine.left === null) return null;
  const base = currency === baseCurrency ? amount : rate ? toBaseAmount(amount, rate) : null;
  if (base === null || !isNegativeMoney(subtractMoney(mine.left, base))) return null;
  const alreadyOver = !isNegativeMoney(negateMoney(mine.left));
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-[20px] border border-line bg-surface p-3 ps-4"
    >
      <TriangleAlert aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-expense" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[13px] font-semibold text-expense">{t('limits.warnTitle')}</span>
        <span className="text-xs text-muted">
          {alreadyOver
            ? t('limits.warnAlreadyOver')
            : t('limits.warnLeft', {
                amount: formatAmount(mine.left, limits.data!.currency, i18n.language),
              })}
        </span>
      </span>
    </div>
  );
}
